import { useMutation } from '@apollo/client/react';
import { Close, Star, StarOutline } from '@mui/icons-material';
import { Dialog, IconButton, Typography, Tab, Grid, Tabs, Stack, Checkbox, type SxProps } from '@mui/material';
import Slide from '@mui/material/Slide';
import { type TransitionProps } from '@mui/material/transitions';
import { without } from 'lodash';
import React, { forwardRef, useContext, useState } from 'react';

import CompanySettingsTab from './CompanySettingsTab';
import PermissionsManagementTab from './PermissionsManagementTab';
import ProfileTab from './ProfileTab';
import ResourcesManagementTab from './ResourcesManagementTab';
import TagsManagementTab from './TagManagementTab';

import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { PermissionActionEnum, PermissionEntityEnum, UpdateUserSettingDocument } from '@/graphql/generated/graphql';

interface SettingsModalProps {
  open: boolean;
  handleClose: () => void;
}

const a11yProps = (index: number) => {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
};
const sxProps: SxProps = { width: '100%', maxWidth: '100%', textAlign: 'left' };

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const SettingsModal: React.FC<SettingsModalProps> = ({ open, handleClose }) => {
  const [mainTab, setMainTab] = useState(0);
  const { currentUser, dashboardLayout } = useContext(CurrentUserContext);
  const { canPerformAction } = useContext(PermissionContext);
  const { View } = PermissionActionEnum;
  const {
    CompanySettings,
    PermissionManagement,
    EmployeeManagement,
    EquipmentManagement,
    SubcontractorManagement,
    TagManagement,
  } = PermissionEntityEnum;

  const [updateUserSetting] = useMutation(UpdateUserSettingDocument);
  const handleMainTabChange = (_: React.SyntheticEvent, newValue: number) => setMainTab(newValue);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  };
  const onFavorite = (tabName: string, checked: boolean) => {
    updateUserSetting({
      context: {
        headers: {
          source: 'settings',
        },
      },
      variables: {
        name: 'favorite_tabs',
        value: (checked
          ? [...(currentUser?.favoriteTabs ?? []), tabName]
          : without(currentUser?.favoriteTabs, tabName)
        ).join(','),
      },
      onCompleted: () => {
        updateUserSetting({
          variables: {
            name: 'dashboard_layout',
            value: (checked ? [...dashboardLayout, tabName] : without(dashboardLayout, tabName)).join(','),
          },
          refetchQueries: ['getCurrentUser'],
        });
      },
    });
  };
  const setTabTitle = (title: string) => {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        {title}
        <Checkbox
          size="small"
          defaultChecked={currentUser?.favoriteTabs?.includes(title)}
          icon={<StarOutline />}
          checkedIcon={<Star />}
          onChange={(_, checked) => onFavorite(title, checked)}
          sx={{ ml: 'auto' }}
        />
      </Stack>
    );
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      disableEscapeKeyDown={false}
      slots={{
        transition: Transition,
      }}
    >
      <IconButton onClick={handleClose} sx={{ position: 'fixed', right: 25, top: 1, zIndex: 500 }}>
        <Close />
      </IconButton>
      <Grid container>
        <Grid sx={{ borderRight: 1, borderColor: 'divider' }} size={2}>
          <Typography align="left" variant="h6" sx={{ p: 1, pl: 2, borderBottom: 1, borderColor: 'divider' }}>
            Settings
          </Typography>
          <Tabs
            value={mainTab}
            orientation="vertical"
            onChange={handleMainTabChange}
            aria-label="Settings Navigation"
            sx={{ '.MuiTab-root': { alignItems: 'flex-start' } }}
          >
            <Tab label="Profile" value={0} {...a11yProps(0)} />
            {canPerformAction(View, CompanySettings) && (
              <Tab label={setTabTitle('Company Settings')} value={1} {...a11yProps(1)} sx={sxProps} />
            )}
            {canPerformAction(View, PermissionManagement) && (
              <Tab label={setTabTitle('Permissions Management')} value={2} {...a11yProps(2)} sx={sxProps} />
            )}
            {canPerformAction(View, [EmployeeManagement, EquipmentManagement, SubcontractorManagement]) && (
              <Tab label={setTabTitle('Resources Management')} value={3} {...a11yProps(3)} sx={sxProps} />
            )}
            {canPerformAction(View, TagManagement) && (
              <Tab label={setTabTitle('Tags Management')} value={4} {...a11yProps(4)} sx={sxProps} />
            )}
          </Tabs>
        </Grid>

        <Grid sx={{ height: '100dvh' }} size={10}>
          <ProfileTab parentTab={mainTab} index={0} />
          {canPerformAction(View, CompanySettings) && <CompanySettingsTab parentTab={mainTab} index={1} />}
          {canPerformAction(View, PermissionManagement) && <PermissionsManagementTab parentTab={mainTab} index={2} />}
          {canPerformAction(View, [EmployeeManagement, EquipmentManagement, SubcontractorManagement]) && (
            <ResourcesManagementTab parentTab={mainTab} index={3} />
          )}
          {canPerformAction(View, TagManagement) && <TagsManagementTab parentTab={mainTab} index={4} />}
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default SettingsModal;
